import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminBrands } from '@r/api/generated/client.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';

const NO_BRAND_VALUE = '__none__';

interface CustomShopProductBrandSelectProps {
  shopId: number;
  value: number | null;
  onChange: (brandId: number | null) => void;
  disabled?: boolean;
}

/** Select des marques disponibles pour une boutique custom. */
export function CustomShopProductBrandSelect({
  shopId,
  value,
  onChange,
  disabled = false
}: CustomShopProductBrandSelectProps) {
  const { t } = useTranslation();
  const brandsQuery = useAdminBrands(String(shopId));
  useOrvalQueryErrorToast(brandsQuery, t('admin.shops.brands.error.load'));

  const brands = useMemo(
    () => [...(brandsQuery.data ?? [])].sort((left, right) => left.name.localeCompare(right.name)),
    [brandsQuery.data]
  );

  return (
    <Select
      disabled={disabled || brandsQuery.isLoading}
      value={value != null ? String(value) : NO_BRAND_VALUE}
      onValueChange={(nextValue) => {
        onChange(nextValue === NO_BRAND_VALUE ? null : Number(nextValue));
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={t('admin.shops.products.form.brand.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_BRAND_VALUE}>{t('admin.shops.products.form.brand.none')}</SelectItem>
        {brands.map((brand) => (
          <SelectItem key={brand.id} value={String(brand.id)}>
            {brand.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
